/**
 * Payment Display Utilities
 *
 * Utility functions to ensure consistent payment method and status display
 * across all components in the application.
 */

import {
  FaCreditCard,
  FaMoneyBillWave,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaUniversity,
  FaWallet
} from 'react-icons/fa';

/**
 * Get standardized payment method display text with icon
 * @param {string} paymentMethod - The payment method from booking data
 * @returns {JSX.Element} Display element with icon
 */
export const getPaymentMethodDisplay = (paymentMethodOrBooking) => {
  let methodInput = "";
  let booking = null;

  if (paymentMethodOrBooking && typeof paymentMethodOrBooking === "object") {
    booking = paymentMethodOrBooking;
    methodInput = booking.paymentMethod || "";
  } else {
    methodInput = paymentMethodOrBooking || "";
  }

  if (!methodInput || typeof methodInput !== "string") {
    return (
      <>
        <FaCreditCard className="inline mr-2" />
        Razorpay
      </>
    );
  }

  const method = methodInput.toLowerCase();

  // Check for partial payment
  if (booking && booking.advancePaid > 0) {
    const isCollected = booking.isPaymentCollected;
    if (!isCollected) {
      return (
        <>
          <FaCreditCard className="inline mr-2" />
          Online Advance (Pending Remaining)
        </>
      );
    } else {
      let remainingText = "Cash";
      let icon = <FaMoneyBillWave className="inline mr-2" />;

      if (method === "wallet") {
        remainingText = "Wallet";
        icon = <FaWallet className="inline mr-2" />;
      } else if (method === "online" || method === "razorpay") {
        remainingText = "Online QR";
        icon = <FaCreditCard className="inline mr-2" />;
      }

      return (
        <>
          {icon}
          Online Advance & {remainingText}
        </>
      );
    }
  }

  if (method === "company" || method === "corporate" || method === "pay via company") {
    return (
      <>
        <FaUniversity className="inline mr-2" />
        Pay via Company
      </>
    );
  }

  if (method === "cod" || method === "cash" || method === "cash on delivery") {
    return (
      <>
        <FaMoneyBillWave className="inline mr-2" />
        Cash Payment
      </>
    );
  }

  if (method === "wallet") {
    return (
      <>
        <FaWallet className="inline mr-2" />
        Wallet Payment
      </>
    );
  }

  // For all online payment methods (ONLINE, online, razorpay, card, upi, etc.)
  return (
    <>
      <FaCreditCard className="inline mr-2" />
      Razorpay
    </>
  );
};

/**
 * Get standardized payment status display text with icon
 * @param {Object} booking - The booking object
 * @returns {JSX.Element} Display element with icon
 */
export const getPaymentStatusDisplay = (booking) => {
  if (!booking || typeof booking !== "object") {
    return (
      <>
        <FaClock className="inline mr-2" />
        Pending
      </>
    );
  }

  const { paymentMethod, status, paymentStatus } = booking;

  // Check for partial payment
  const advancePaid = booking.advancePaid || 0;
  const isCollected = booking.isPaymentCollected;
  if (advancePaid > 0) {
    if (!isCollected) {
      return (
        <>
          <FaClock className="inline mr-2 text-blue-600" />
          <span className="text-blue-600">Advance Paid</span>
        </>
      );
    } else {
      return (
        <>
          <FaCheckCircle className="inline mr-2 text-green-600" />
          <span className="text-green-600">Paid</span>
        </>
      );
    }
  }

  const method = typeof paymentMethod === 'string' ? paymentMethod.toLowerCase() : '';
  const isCompanyBilling =
    method === 'company' ||
    booking.createdByType === 'corporate_staff' ||
    !!booking.corporateAccountId ||
    !!booking.corporateMemberId;

  if (isCompanyBilling) {
    const normalizedStatus = String(status || '').toLowerCase();
    if (normalizedStatus === 'pending_corporate_approval') {
      return (
        <>
          <FaClock className="inline mr-2" />
          Pending Corporate Approval
        </>
      );
    }
    if (normalizedStatus === 'approved_by_corporate') {
      return (
        <>
          <FaCheckCircle className="inline mr-2 text-green-600" />
          <span className="text-green-600">Company Approved</span>
        </>
      );
    }

    return (
      <>
        <FaClock className="inline mr-2" />
        Billed to Company
      </>
    );
  }

  // For COD payments - check if ride is completed OR payment status is completed
  if (
    paymentMethod &&
    typeof paymentMethod === "string" &&
    (paymentMethod.toLowerCase() === "cod" ||
      paymentMethod.toLowerCase() === "cash" ||
      paymentMethod.toLowerCase() === "cash on delivery")
  ) {
    // If payment status is explicitly "completed" OR ride is completed, show as Paid
    if (
      (paymentStatus && paymentStatus.toLowerCase() === "completed") ||
      (status && (status.toLowerCase() === "completed" || status.toLowerCase() === "finished"))
    ) {
      return (
        <>
          <FaCheckCircle className="inline mr-2 text-green-600" />
          <span className="text-green-600">Paid</span>
        </>
      );
    }
    // Otherwise show as Pending
    return (
      <>
        <FaClock className="inline mr-2" />
        Pending
      </>
    );
  }

  // For wallet payments - show as Paid (instant payment)
  if (
    paymentMethod &&
    typeof paymentMethod === "string" &&
    paymentMethod.toLowerCase() === "wallet"
  ) {
    return (
      <>
        <FaCheckCircle className="inline mr-2 text-green-600" />
        <span className="text-green-600">Paid</span>
      </>
    );
  }

  // For all online payments - show only "Paid" without additional text
  return (
    <>
      <FaCheckCircle className="inline mr-2 text-green-600" />
      <span className="text-green-600">Paid</span>
    </>
  );
};

/**
 * Check if payment method is online/digital
 * @param {string} paymentMethod - The payment method
 * @returns {boolean} True if online payment
 */
export const isOnlinePayment = (paymentMethod) => {
  if (!paymentMethod || typeof paymentMethod !== "string") return true; // Default to online

  const method = paymentMethod.toLowerCase();
  if (method === 'company' || method === 'corporate' || method === 'pay via company') return false;
  return !(
    method === "cod" ||
    method === "cash" ||
    method === "cash on delivery"
  );
};

/**
 * Get payment method badge color class
 * @param {string} paymentMethod - The payment method
 * @returns {string} CSS class for styling
 */
export const getPaymentMethodBadgeClass = (paymentMethodOrBooking) => {
  let method = "";
  let booking = null;

  if (paymentMethodOrBooking && typeof paymentMethodOrBooking === 'object') {
    booking = paymentMethodOrBooking;
    method = booking.paymentMethod || "";
  } else {
    method = paymentMethodOrBooking || "";
  }

  if (booking && booking.advancePaid > 0) {
    return 'bg-indigo-100 text-indigo-800'; // Special badge style for split/partial payment
  }

  if (method && typeof method === 'string') {
    const methodLower = method.toLowerCase();
    if (methodLower === 'company' || methodLower === 'corporate' || methodLower === 'pay via company') {
      return 'bg-indigo-100 text-indigo-800';
    }
    if (methodLower === 'wallet') {
      return 'bg-purple-100 text-purple-800';
    }
  }
  if (isOnlinePayment(method)) {
    return "bg-blue-100 text-blue-800";
  }
  return "bg-yellow-100 text-yellow-800";
};

/**
 * Get payment status badge color class
 * @param {Object} booking - The booking object
 * @returns {string} CSS class for styling
 */
export const getPaymentStatusBadgeClass = (booking) => {
  if (!booking || typeof booking !== "object") {
    return "bg-yellow-100 text-yellow-800"; // Default to pending
  }

  const { paymentMethod, status, paymentStatus } = booking;

  // Check for partial payment
  const advancePaid = booking.advancePaid || 0;
  const isCollected = booking.isPaymentCollected;
  if (advancePaid > 0) {
    if (!isCollected) {
      return "bg-blue-100 text-blue-800";
    } else {
      return "bg-green-100 text-green-800";
    }
  }

  const method = typeof paymentMethod === 'string' ? paymentMethod.toLowerCase() : '';
  const isCompanyBilling =
    method === 'company' ||
    booking.createdByType === 'corporate_staff' ||
    !!booking.corporateAccountId ||
    !!booking.corporateMemberId;

  if (isCompanyBilling) {
    const normalizedStatus = String(status || '').toLowerCase();
    if (normalizedStatus === 'approved_by_corporate') {
      return 'bg-green-100 text-green-800';
    }
    return 'bg-yellow-100 text-yellow-800';
  }

  // For COD payments - check if ride is completed OR payment status is completed
  if (
    paymentMethod &&
    typeof paymentMethod === "string" &&
    (paymentMethod.toLowerCase() === "cod" ||
      paymentMethod.toLowerCase() === "cash" ||
      paymentMethod.toLowerCase() === "cash on delivery")
  ) {
    // If payment status is explicitly "completed" OR ride is completed, show green (Paid)
    if (
      (paymentStatus && paymentStatus.toLowerCase() === "completed") ||
      (status && (status.toLowerCase() === "completed" || status.toLowerCase() === "finished"))
    ) {
      return "bg-green-100 text-green-800";
    }
    // Otherwise show yellow (Pending)
    return "bg-yellow-100 text-yellow-800";
  }

  // For all online payments - show as Paid (green)
  return "bg-green-100 text-green-800";
};

/**
 * Get payment method icon component
 * @param {string} paymentMethod - The payment method
 * @param {string} className - Optional CSS class for the icon
 * @returns {JSX.Element} React icon component
 */
export const getPaymentMethodIcon = (paymentMethod, className = "inline mr-2") => {
  if (paymentMethod && typeof paymentMethod === 'string') {
    const method = paymentMethod.toLowerCase();
    if (method === 'company' || method === 'corporate' || method === 'pay via company') {
      return <FaUniversity className={className} />;
    }
    if (method === 'wallet') {
      return <FaWallet className={className} />;
    }
  }
  if (isOnlinePayment(paymentMethod)) {
    return <FaCreditCard className={className} />;
  }
  return <FaMoneyBillWave className={className} />;
};

/**
 * Get payment status icon component
 * @param {Object} booking - The booking object
 * @returns {JSX.Element} React icon component
 */
export const getPaymentStatusIcon = (booking) => {
  if (!booking || typeof booking !== "object") {
    return <FaClock className="inline mr-2" />; // Default to pending
  }

  const { paymentMethod, status, paymentStatus } = booking;

  // Check for partial payment
  const advancePaid = booking.advancePaid || 0;
  const isCollected = booking.isPaymentCollected;
  if (advancePaid > 0) {
    if (!isCollected) {
      return <FaClock className="inline mr-2 text-blue-600" />;
    } else {
      return <FaCheckCircle className="inline mr-2 text-green-600" />;
    }
  }

  const method = typeof paymentMethod === 'string' ? paymentMethod.toLowerCase() : '';
  const isCompanyBilling =
    method === 'company' ||
    booking.createdByType === 'corporate_staff' ||
    !!booking.corporateAccountId ||
    !!booking.corporateMemberId;

  if (isCompanyBilling) {
    const normalizedStatus = String(status || '').toLowerCase();
    if (normalizedStatus === 'approved_by_corporate') {
      return <FaCheckCircle className="inline mr-2" />;
    }
    return <FaClock className="inline mr-2" />;
  }

  // For COD payments - check if ride is completed OR payment status is completed
  if (
    paymentMethod &&
    typeof paymentMethod === "string" &&
    (paymentMethod.toLowerCase() === "cod" ||
      paymentMethod.toLowerCase() === "cash" ||
      paymentMethod.toLowerCase() === "cash on delivery")
  ) {
    // If payment status is explicitly "completed" OR ride is completed, show checkmark
    if (
      (paymentStatus && paymentStatus.toLowerCase() === "completed") ||
      (status && (status.toLowerCase() === "completed" || status.toLowerCase() === "finished"))
    ) {
      return <FaCheckCircle className="inline mr-2" />;
    }
    // Otherwise show clock (Pending)
    return <FaClock className="inline mr-2" />;
  }

  // For all online payments - show as Paid
  return <FaCheckCircle className="inline mr-2" />;
};
